import { ColDef, type ICellRendererParams } from "ag-grid-community";
import { DynamoDbCapacityModeCellRenderer } from "./DynamoDbCapacityModeCellRenderer";
import { dynamoDbCapacityModeValueGetter } from "./dynamoDbCapacityModeValueGetter";
import { jiraIssuesColumnDef } from "../../common/jiraIssuesColumnDef";
import { tagsColumnDef } from "../../common/tagsColumnDef";
import { smartTagsColumnDef } from "../../common/smartTagsColumnDef";
import { regionColumnDef } from "../../common/regionColumnDef";
import { resourceCreatedByColumnDef } from "../../common/resourceCreatedByColumnDef";
import { resourceTotalSpendColumnDef } from "../../common/resourceTotalSpendColumnDef";
import { getResourceCreationTimeColumnDef } from "../../common/getResourceCreationTimeColumnDef";
import { forecastedEOMColumnDef } from "../../common/forecastedEOMColumnDef";
import { resourceAccountColumnDef } from "../../common/resourceAccountColumnDef";
import { getResourceSizeColumnDef } from "../../common/getResourceSizeColumnDef";
import { FilteredDynamoDbTableResourceItem } from "@/services/cloudchipr.api";
import { getResourceIdentifierColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/getResourceIdentifierColumnDef";
import { taskColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/taskColumnDef";
import { billingCostColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/billingCostColumnDef";
import { getListingPriceColumnDef } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/listingPriceColumnDef";

export const dynamoDbTableColumnDefs: ColDef[] = [
  getResourceIdentifierColumnDef(),

  forecastedEOMColumnDef,
  billingCostColumnDef,
  getListingPriceColumnDef("dynamo_db_table"),

  resourceAccountColumnDef,

  getResourceCreationTimeColumnDef("Date Created"),

  resourceTotalSpendColumnDef,

  getResourceSizeColumnDef({
    propName: "size",
    headerName: "Table Size",
  }),

  {
    field: "resource.table_class",
    cellRendererParams: { noData: "N/A" },
    headerName: "Table Class",
  },

  {
    field: "resource.provider_status",
    headerName: "Status",
  },

  {
    field: "resource.replicas",
    headerName: "Replicas",
    cellRendererParams: { noData: 0 },
  },

  {
    field: "resource.read_usage_value",
    headerTooltip: "Max read usage for the last 7 days.",
    headerName: "Read Usage",
    valueGetter: ({ data }) => {
      if (!data) {
        return null;
      }

      return ` ${data?.resource?.read_usage_value} ${data?.resource?.capacity_mode === "Provisioned" ? "%" : "RCU"}`;
    },
    cellRenderer: ({
      data,
    }: ICellRendererParams<FilteredDynamoDbTableResourceItem>) => {
      if (!data) {
        return null;
      }

      return ` ${data?.resource?.read_usage_value} ${data?.resource?.capacity_mode === "Provisioned" ? "%" : "RCU"}`;
    },
  },

  {
    field: "resource.rcu_reservation_value",
    headerTooltip: "Current RCU reservation.",
    headerName: "RCU Reservation",
    valueGetter: ({ data }) => {
      const value = data?.resource?.rcu_reservation_value;
      return value ? `${value} RCU` : "-";
    },
    cellRenderer: ({
      data,
    }: ICellRendererParams<FilteredDynamoDbTableResourceItem>) => {
      const value = data?.resource?.rcu_reservation_value;
      return value ? `${value} RCU` : "-";
    },
  },

  {
    field: "resource.write_usage_value",
    headerTooltip: "Max write usage for the last 7 days.",
    headerName: "Write Usage",
    valueGetter: ({ data }) => {
      const capacity_mode = data?.resource?.capacity_mode;
      const value = data?.resource?.write_usage_value;

      if (!capacity_mode) {
        return "-";
      }

      return `${value} ${capacity_mode === "Provisioned" ? "%" : "WCU"}`;
    },

    cellRenderer: ({
      data,
    }: ICellRendererParams<FilteredDynamoDbTableResourceItem>) => {
      const capacity_mode = data?.resource?.capacity_mode;
      const value = data?.resource?.write_usage_value;

      if (!capacity_mode) {
        return "-";
      }

      return ` ${value} ${capacity_mode === "Provisioned" ? "%" : "WCU"}`;
    },
  },

  {
    field: "resource.wcu_reservation_value",
    headerTooltip: "Current RCU reservation.",
    headerName: "RCU Reservation",
    valueGetter: ({ data }) => {
      const value = data?.resource?.wcu_reservation_value;
      return value ? `${value} WCU` : "-";
    },
    cellRenderer: ({
      data,
    }: ICellRendererParams<FilteredDynamoDbTableResourceItem>) => {
      const value = data?.resource?.wcu_reservation_value;
      return value ? `${value} WCU` : "-";
    },
  },

  {
    field: "resource.read_capacity_mode",
    headerName: "Read Capacity Mode",
    valueGetter: dynamoDbCapacityModeValueGetter,
    cellRenderer: DynamoDbCapacityModeCellRenderer,
  },

  {
    field: "resource.write_capacity_mode",
    headerName: "Write Capacity Mode",
    valueGetter: dynamoDbCapacityModeValueGetter,
    cellRenderer: DynamoDbCapacityModeCellRenderer,
  },

  resourceCreatedByColumnDef,

  regionColumnDef,
  tagsColumnDef,
  smartTagsColumnDef,
  taskColumnDef,
  jiraIssuesColumnDef,
];
